import { firstNumber, firstPresent } from './chatArtifactValueUtils';

const ANSWER_BY_ENRICHING = 'answer_by_enriching';
const RESTORABLE_OFFER_STATUSES = new Set(['awaiting_approval', 'cancelled']);

// Completion closes the proposal that ran, never the composer's generic action.
// History carries the flag on every turn for the list; a later proposal stays open.
export function ownerOfferActions(actions, artifact, { history, index } = {}) {
  const list = Array.isArray(actions) ? actions : [];
  if (artifact?.owner_run_completed !== true) return list;
  const completedAfterProposal = !Array.isArray(history) || history.slice(index).some((message) => (
    (message?.artifact?.artifact_id || message?.artifact_id) === artifact.artifact_id &&
    message?.job_type === 'artifact_lead_enrichment' &&
    (message.job_status === 'done' || message.job_status === 'completed_with_errors')
  ));
  return completedAfterProposal
    ? list.filter((action) => !(action?.proposed && action.key === 'lead_finder_contacts'))
    : list;
}

// The frozen price of a paid enrichment offer (BE build_quote: rows x per-company
// price = total). Null when the payload carries no usable quote.
export function offerQuote(proposal) {
  const quote = proposal?.quote;
  const rowCount = firstNumber(quote?.row_count);
  const perCompany = firstNumber(quote?.credit_cost_per_company, proposal?.credit_cost_per_company);
  const total = firstNumber(quote?.total_credits);
  if (rowCount === undefined || perCompany === undefined || total === undefined) return null;
  return { rowCount, perCompany, total, isMaximum: quote?.total_is_maximum === true };
}

// Mirrors BE free_text_proposal_message, which a restored turn loses: history
// replaces the offer text with the job status message.
function offerText(proposal) {
  const quote = offerQuote(proposal);
  const request = firstPresent(proposal?.enrichment_request);
  if (!quote || !request) return null;
  const noun = quote.rowCount === 1 ? 'company' : 'companies';
  const scope = quote.rowCount === 1 ? 'the 1 saved company' : `all ${quote.rowCount} saved ${noun}`;
  return `I can research ${request} for ${scope}. It costs ${quote.perCompany} credits per company, ` +
    `${quote.total} credits in total, and nothing is charged until you approve.`;
}

// The same action the live turn renders (BE answer_by_enriching_suggested_action).
function offerAction(proposal, artifactId) {
  return {
    key: ANSWER_BY_ENRICHING,
    query: proposal.enrichment_request,
    scope: firstPresent(proposal.scope, 'all_candidates'),
    scope_label: proposal.scope_label,
    requires_confirmation: true,
    credit_cost_per_company: proposal.credit_cost_per_company,
    quote: proposal.quote,
    endpoint: {
      method: 'POST',
      path: `/chat/artifacts/${encodeURIComponent(artifactId)}/actions/${ANSWER_BY_ENRICHING}/submit`,
    },
  };
}

// A restored durable enrichment offer: show the priced offer like the live turn,
// keep the enrich control only while the offer still awaits approval, and keep
// the backend's outcome ("Approval expired.") as a notice.
export function restoredOfferPatch(message) {
  const proposal = message?.artifact_action_proposal;
  if (!message?.job_id || !RESTORABLE_OFFER_STATUSES.has(message.job_status)) return {};
  const content = offerText(proposal);
  const artifactId = firstPresent(proposal?.artifact_id, message.artifact_id);
  if (!content || !artifactId) return {};
  const awaiting = message.job_status === 'awaiting_approval';
  return {
    content,
    notice: awaiting ? null : message.content || null,
    artifactAnswerActions: awaiting ? [offerAction(proposal, artifactId)] : [],
  };
}

// Price shown beside an enrich control (sweep D17): the quoted total when the
// offer froze one, else the per-company price every enrich action carries.
// FE #748: the short price a paid control carries on its button.
export function offerButtonCostLabel(action) {
  const quote = offerQuote(action);
  if (quote) return `${quote.isMaximum ? 'up to ' : ''}${quote.total} credits`;
  if (action?.cost_label) return action.cost_label;
  const perCompany = firstNumber(action?.credit_cost_per_company);
  return perCompany === undefined ? null : `${perCompany} credits per company`;
}

export function offerCostLabel(action) {
  const quote = offerQuote(action);
  if (quote) return `${quote.perCompany} credits per company, ${quote.total} credits total`;
  const perCompany = firstNumber(action?.credit_cost_per_company);
  return perCompany === undefined ? null : `${perCompany} credits per company`;
}

// The live offer carries its frozen quote on the proposal, not on the action.
// FE #762: the action a turn proposes (the paid owners lookup of a named-companies
// list, #901 on a saved one) is marked, so the turn renders it with its price.
export function withOfferQuote(actions, proposal) {
  const list = Array.isArray(actions) ? actions : [];
  if (!proposal?.action_key) return list;
  return list.map((action) => {
    if (action?.key !== proposal.action_key) return action;
    return {
      ...action,
      proposed: true,
      ...(!action.quote && proposal.quote ? { quote: proposal.quote } : {}),
      // #933: a row-scoped offer names its admitted companies; a whole-list one names none.
      ...(Array.isArray(proposal.company_ids) ? { proposal_company_ids: proposal.company_ids } : {}),
    };
  });
}
